import { NotificationService, useIntl } from '@capsicum/common';
import { ModalService, Notification, useRouter } from '@capsicum/common-web';
import {
	useCancelOrderMutation,
	useTransactionCancellationData,
	useWalletNotificationAtom
} from '@capsicum/features/wallet/shared';
import { useUserTransactionHistoryQueryCacheInvalidation } from 'libs/features/wallet/shared/src/data-access';
import { useCallback, useMemo } from 'react';

import { CancelOrderDrawer } from '../../ui';

export type OrderCancellation = {
	handleClickCancelOrder: () => void;
	isCancellable: boolean;
};

export const useOrderCancellation = (transactionId: string): OrderCancellation => {
	const { $t } = useIntl();
	const { push } = useRouter();
	const { setWalletNotification } = useWalletNotificationAtom();
	const { mutate: doCancel } = useCancelOrderMutation();
	const invalidateHistoryCache = useUserTransactionHistoryQueryCacheInvalidation();

	const { isCancellable, orderId } = useTransactionCancellationData(transactionId);

	const handleSuccessCancelOrder = useCallback(() => {
		invalidateHistoryCache();
		push('/wallet');
		setWalletNotification({
			description: $t('wallet.partnerWallet.onHold.cancelSuccessDesc'),
			title: $t('wallet.partnerWallet.onHold.cancelSuccessTitle'),
			type: 'success'
		});
	}, [$t, invalidateHistoryCache, push, setWalletNotification]);

	const handleErrorCancelOrder = useCallback(() => {
		NotificationService.addNotification({
			component: (
				<Notification
					message={$t('wallet.partnerWallet.onHold.cancelErrorDesc')}
					title={$t('wallet.partnerWallet.onHold.cancelErrorTitle')}
					variant="error"
				/>
			)
		});
	}, [$t]);

	const handleClickCancelOrder = useCallback(() => {
		const cancelOrder = () => {
			doCancel(orderId ?? '', {
				onError: handleErrorCancelOrder,
				onSuccess: handleSuccessCancelOrder
			});
		};

		ModalService.addModal({
			renderComponent: ({ close }) => (
				<CancelOrderDrawer
					cancelOrder={cancelOrder}
					closeModal={close}
					testID="partnerWallet-onHold-cancelOrderDrawer"
				/>
			)
		});
	}, [doCancel, handleErrorCancelOrder, handleSuccessCancelOrder, orderId]);

	return useMemo(
		() => ({
			handleClickCancelOrder,
			isCancellable
		}),
		[handleClickCancelOrder, isCancellable]
	);
};
